<template>
  <div class="w-full flex flex-col gap-8 text-left" data-testid="approvals-hub">
    <section
      v-for="group in groups"
      :key="group.key"
      class="flex flex-col gap-4"
      :data-testid="`approvals-hub-group-${group.key}`"
    >
      <!-- Título e nota do grupo são um par colado; o respiro fica entre o par e os cards. -->
      <div>
        <h2 class="text-base leading-6 font-bold text-textPrimary">{{ group.title }}</h2>
        <p class="text-sm leading-[18px] text-neutral50">{{ group.note }}</p>
      </div>

      <!-- Grid fluido como o `.me-grid` do protótipo: colunas de no mínimo 320px que
           se repartem a largura disponível. Com 2 filas visíveis os cards ocupam
           a linha inteira, em vez de deixar a terceira coluna vazia. -->
      <div class="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-3">
        <ApprovalQueueCard
          v-for="queue in group.queues"
          :key="queue.key"
          :icon="queue.icon"
          :title="queue.cardTitle"
          :description="queue.cardDescription"
          :count-label="queue.counterLabel"
          :count="countFor(queue.key).value"
          :is-loading="countFor(queue.key).isLoading"
          :error-message="countFor(queue.key).error"
          :badge="queue.badge ?? null"
          :footer-text="footerOf(queue)?.text ?? null"
          :footer-icon="footerOf(queue)?.icon ?? null"
          :footer-variant="footerOf(queue)?.variant ?? 'default'"
          :test-id="queue.testId"
          @click="router.push(queue.path)"
        />
      </div>
    </section>

    <!-- Painéis (Pós-plantão): um card só, na largura inteira, por isso sem o
         grid dos grupos de fila. Cada painel é um grupo próprio, com título e
         nota como os grupos acima. -->
    <section
      v-for="panel in panels"
      :key="panel.key"
      class="flex flex-col gap-4"
      :data-testid="`approvals-hub-group-${panel.key}`"
    >
      <div>
        <h2 class="text-base leading-6 font-bold text-textPrimary">{{ panel.groupTitle }}</h2>
        <p class="text-sm leading-[18px] text-neutral50">{{ panel.groupNote }}</p>
      </div>

      <ApprovalPanelCard
        :icon="panel.icon"
        :title="panel.title"
        :description="panel.description"
        :test-id="panel.testId"
        @click="router.push(panel.path)"
      />
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'

import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useApprovalsHubStore } from '@/pinia-store/useApprovalsHubStore'
import { useDsAttendanceStore } from '@/pinia-store/useDsAttendanceStore'
import { useHeaderStore } from '@/pinia-store/useHeaderStore'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import type { Authorizations } from '@/types/authorization'

import ApprovalPanelCard from './components/ApprovalPanelCard.vue'
import ApprovalQueueCard from './components/ApprovalQueueCard.vue'
import {
  APPROVALS_QUEUE_GROUPS,
  approvalsQueueCard,
  queuesInGroup,
  visibleApprovalsPanels,
  visibleApprovalsQueues
} from './queues'
import type { ApprovalsQueue, ApprovalsQueueKey } from './queues'

const router = useRouter()
const headerStore = useHeaderStore()
const hubStore = useApprovalsHubStore()
const { flags } = useFeatureFlags()

// Q27 — a fila "Avaliação de Evidências" some do hub quando o backend informa
// que a inserção manual está bloqueada. A ROTA continua viva: quem tem a URL
// salva cai na tela com a faixa de aposentada, nunca num 404.
const dsAttendanceStore = useDsAttendanceStore()
const permissionsStore = usePermissionsStore()

// Mesma regra da guarda de rota (OR entre as autorizações da fila): card só da
// fila que o usuário consegue abrir — e contador só dela.
const canAccess = (authorizations: Authorizations) =>
  authorizations.some((authorization) => permissionsStore.checkAnyRessources(authorization))

const queues = computed(() => {
  const isRetired = dsAttendanceStore.isManualCheckBlocked
  return visibleApprovalsQueues(flags, { isCheckValidationRetired: isRetired, canAccess }).map(
    (queue) => approvalsQueueCard(queue, isRetired)
  )
})
// Painéis (Pós-plantão) vindos de main (#2729): card único, fora dos grupos de fila.
const panels = computed(() => visibleApprovalsPanels(flags))

// Grupo sem fila ligada não aparece (evita título órfão quando as flags do grupo
// inteiro estão desligadas).
const groups = computed(() =>
  APPROVALS_QUEUE_GROUPS.map((group) => ({
    ...group,
    queues: queuesInGroup(queues.value, group.key)
  })).filter((group) => group.queues.length > 0)
)

const countFor = (key: ApprovalsQueueKey) => hubStore.countFor(key)
// Rodapé real (backend) quando a fila já o tem; senão o texto do registro.
const footerOf = (queue: ApprovalsQueue) => hubStore.footerFor(queue.key) ?? queue.footer

onMounted(() => {
  headerStore.setTitle('Aprovações', 'Escolha a fila que você quer analisar')
  // Tolerante a backend antigo: falha volta ao default (nenhuma fila aposentada).
  void dsAttendanceStore.fetchRegistrationSettings()
  // Uma request leve por fila; falha vira "—" no card sem travar a navegação.
  void hubStore.fetchCounts(queues.value.map((queue) => queue.key))
})
</script>
